import { paymentWorkflowAccess } from "./booking/paymentWorkflow.js";
import type { Me } from "./api.js";
import type { SidebarItem } from "./Sidebar.js";
import { canManageSchoolDomain, isOwner } from "./auth.js";
import { actionCentreAccess, hasActionCentreAccess } from "./actionCentre.js";
import { hasWorkspaceReport } from "./pages/Reports.js";

/**
 * Build the staff navigation around the jobs somebody came to the Hub to do.
 *
 * Routes and permissions stay exactly where they are; this only decides which
 * destinations deserve first-level prominence and which belong to a shared
 * workspace. Keeping the capability checks here also means desktop and mobile
 * receive the same already-filtered hierarchy from the shared Sidebar.
 */
/** What the DBS count on the Staff badge is counting. */
function dbsErasureWarning(count: number): string {
  return `${count} DBS ${count === 1 ? "record" : "records"} past the six months the school keeps them for`;
}

/**
 * Existing owner/tutor access to Today and its teaching utilities. Keep this
 * separate from display prominence: changing the default staff home must not
 * remove an already-authorised route.
 */
export function hasTodayWorkspace(me: Me): boolean {
  return isOwner(me) || me.roles.includes("tutor");
}

/**
 * Whether Today is the right default staff landing page.
 *
 * A tutor role or the server's provider link is a real teaching signal. An
 * owner role by itself is not: owners without a provider only start somewhere
 * else. Today stays in their navigation, because the route is still theirs.
 */
export function hasTeachingDisplaySignal(me: Me): boolean {
  return me.roles.includes("tutor") || me.linkedToProvider;
}

/** Select the best currently available staff landing page from `/me` signals. */
export function staffLandingPath(me: Me): string {
  if (hasTeachingDisplaySignal(me)) return "/today";
  return hasActionCentreAccess(actionCentreAccess(me)) ? "/waiting" : "/diary";
}

export function staffSidebarItems(me: Me, neckDiagramsEnabled = false): SidebarItem[] {
  const held = new Set(me.capabilities);
  const owner = isOwner(me);
  const paymentAccess = paymentWorkflowAccess(me);
  const actionCentrePath = me.booking.staff ? "/waiting" : "/queue";

  return [
    ...(hasTodayWorkspace(me)
      ? [
          {
            to: "/today",
            label: "Today",
            // Keep old bookmarks and browser history visibly attached to the
            // renamed destination while App redirects them to /today.
            activeFor: ["/dashboard"],
          } satisfies SidebarItem,
        ]
      : []),
    { to: "/diary", label: "Diary" },
    // Tutors receive only their server-scoped roster. Keep that visible in the
    // navigation instead of implying that /students is a whole-school list.
    { to: "/students", label: owner ? "Students" : "My students" },
    ...(me.groupActivityAttendanceEnabled === true && hasTodayWorkspace(me)
      ? [{ to: "/group-activities", label: "Group activities" } satisfies SidebarItem]
      : []),
    { to: "/inbox", label: "Inbox" },
    {
      to: actionCentrePath,
      label: "Action Centre",
      activeFor: me.booking.staff ? ["/queue"] : undefined,
    },
    // Reports covers students, attendance, credits, earnings and utilisation,
    // not only payments, so it stays first-level rather than inside Payments,
    // where folding that group away would hide every report.
    ...(me.booking.staff && hasWorkspaceReport(held)
      ? [{ to: "/reports", label: "Reports" } satisfies SidebarItem]
      : []),
    ...(owner && me.enquiryPipelineEnabled === true ? [{ to: "/enquiries", label: "Enquiries" } satisfies SidebarItem] : []),
    // First-level destinations must stay together. Sidebar groups consecutive
    // items, so putting Schools after Schedule would create an orphan headless
    // section between Schedule and Admin rather than another first-level job.
    ...(canManageSchoolDomain(me)
      ? [
          {
            to: "/schools",
            label: "Schools",
            // School grooves live under the same destination rather than as a
            // second entry: Sidebar groups consecutive items, and School is one
            // job with several screens.
            activeFor: [
              "/school-payers",
              "/school-invoices",
              "/school-credits",
              "/school-grooves",
            ],
          } satisfies SidebarItem,
        ]
      : []),
    { to: "/help", label: "Help & guides" },
    ...(hasTodayWorkspace(me)
      ? [
          { to: "/my-practice", label: "My Practice", group: "Teaching" } satisfies SidebarItem,
          { to: "/practice-history", label: "Practice tracking", group: "Teaching" } satisfies SidebarItem,
          { to: "/practice-leaderboard", label: "Leaderboard", group: "Teaching" } satisfies SidebarItem,
        ]
      : []),
    ...(hasTodayWorkspace(me) ? [{ to: "/repertoire", label: "Repertoire", group: "Teaching" } satisfies SidebarItem] : []),
    // Keep this distinct from the established per-student Resources system for
    // files and links. Groove Builder is the first teaching tool here; more
    // interactive teaching utilities can join it without new sidebar entries.
    { to: "/grooves", label: "Teaching Tools", group: "Teaching" },
    ...(neckDiagramsEnabled
      ? [{ to: "/neck-diagrams", label: "My diagrams", group: "Teaching" } satisfies SidebarItem]
      : []),
    ...(hasTodayWorkspace(me) ? [{ to: "/files", label: "Files", group: "Teaching" }] : []),
    ...(me.booking.staff ? [
      ...(me.paymentInvoices ? [{ to: "/payments/invoices", label: "Paid invoices", group: "Payments" }] : []),
      ...(paymentAccess.lessons || paymentAccess.blocks
        ? [{ to: "/payments/log", label: "Log payment", group: "Payments" }] : []),
      ...(held.has("reports.payments.read") ? [
        { to: "/reports/unpaid-lessons", label: "Outstanding payments", group: "Payments" },
        { to: "/payments/history", label: "Payment history", group: "Payments" },
      ] : []),
    ] : []),
    ...(me.booking.staff
      ? [
          { to: "/availability", label: "Availability", group: "Schedule" },
          { to: "/time-off", label: "Time off", group: "Schedule" },
          {
            to: "/recurring-lessons",
            label: "Recurring lessons",
            group: "Schedule",
          } satisfies SidebarItem,
        ]
      : []),
    ...(owner
      ? [
          { to: "/calday-room/manage", label: "Calday Room", group: "Admin" } satisfies SidebarItem,
          {
            // What the school pays its tutors. Owners only, and in Admin
            // rather than under Reports: Reports is what the school earns,
            // and a tutor holding a reports capability must never arrive here.
            to: "/invoicing",
            label: "Tutor invoicing",
            group: "Admin",
          } satisfies SidebarItem,
          ...(me.concertsEnabled ? [{ to: "/concerts", label: "Concerts", group: "Admin" } satisfies SidebarItem] : []),
          ...(me.groupActivityAttendanceEnabled === true
            ? [{ to: "/group-activity-management", label: "Manage activities", group: "Admin" } satisfies SidebarItem]
            : []),
          {
            to: "/staff",
            label: "Staff",
            group: "Admin",
            badge:
              me.dbsDueForErasure > 0 ? (
                <span
                  className="tag warn"
                  style={{ marginLeft: 8 }}
                  // The count alone is a number with nothing to say what it
                  // counts. A sighted reader has the warning colour and the
                  // tooltip; the label gives everybody else the same sentence,
                  // and it is what the heading announces when Admin is folded
                  // away and the badge rides up onto it.
                  aria-label={dbsErasureWarning(me.dbsDueForErasure)}
                  title={dbsErasureWarning(me.dbsDueForErasure)}
                >
                  {me.dbsDueForErasure}
                </span>
              ) : undefined,
          },
          {
            to: "/teaching-assignments",
            label: "Teaching assignments",
            group: "Admin",
          },
        ] satisfies SidebarItem[]
      : []),
    { to: "/settings", label: "Settings", group: "Admin" },
  ];
}

